import React, { useState, useCallback } from 'react';
import { AlertCircle, MessageCircleWarning, MessageSquare, ChevronRight, Phone, ExternalLink, Send, X, ThumbsUp } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { notify } from '../../utils/notify';
import { useBackHandler } from '../../hooks/NavigationContext';
import ToolHeader from '../shared/ToolHeader';
import { Section, Card, IconTile } from '../hospital/shared/primitives';
import changelog from '../../changelog.json';

/*
 * Kudos & Concerns: the BCM Integrity Hotline, the formal professionalism form (REDCap) and quick
 * ad hoc feedback about a hospital, a faculty member or a trainee. Same header, cards and
 * bottom sheet as the rest of the app.
 */

const ACCENT = '#b45309';
const HOTLINE = '1-888-294-8970';
const HOTLINE_PORTAL = 'https://www.mycompliancereport.com/report?cid=BCM';
const REDCAP_FORM = 'https://redcap.research.bcm.edu/redcap/surveys/?s=AMYAXRN974C8MC7J';

const rowClass = 'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors';
const input = 'w-full px-3 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:border-amber-600 focus:outline-none disabled:opacity-50';

const Row = ({ icon, title, subtitle, href, onClick }) => {
  const body = (
    <>
      <IconTile icon={icon} accent={ACCENT} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[15px] text-gray-900 dark:text-white">{title}</div>
        {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{subtitle}</div>}
      </div>
      {href ? <ExternalLink size={16} className="text-gray-300 dark:text-gray-600 flex-shrink-0" /> : <ChevronRight size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />}
    </>
  );
  return href
    ? <a href={href} target={href.startsWith('tel:') ? undefined : '_blank'} rel="noopener noreferrer" className={rowClass}>{body}</a>
    : <button type="button" onClick={onClick} className={rowClass}>{body}</button>;
};

const ConcernsScreen = ({ onBack }) => {
  const [open, setOpen] = useState(false);
  const [about, setAbout] = useState('hospital');
  const [tone, setTone] = useState('praise');
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useBackHandler(open, close);

  const submit = async () => {
    if (!text.trim()) { notify.warn('Write the feedback first.'); return; }
    setSending(true);
    try {
      await addDoc(collection(db, 'feedback'), {
        type: about,
        tone,
        notes: text.trim(),
        name: name.trim() || 'Anonymous',
        page: 'Concerns',
        timestamp: serverTimestamp(),
        appVersion: changelog.currentVersion,
        userAgent: navigator.userAgent,
      });
      setSent(true);
      setText(''); setName('');
      setTimeout(() => { setOpen(false); setSent(false); }, 1800);
    } catch (e) {
      console.error(e);
      notify.error('Could not send. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <ToolHeader title="Kudos & Concerns" subtitle="Praise, feedback and formal reports" icon={MessageCircleWarning} accent={ACCENT} backLabel="Home" onBack={onBack} />
      <div className="p-6 space-y-6">
        <Section title="Ad Hoc Feedback">
          <Card>
            <Row icon={MessageSquare} title="Quick note or kudos" subtitle="Praise or a small concern about a hospital, a faculty member or a trainee. Reviewed by department leadership; not a formal record." onClick={() => setOpen(true)} />
          </Card>
        </Section>

        <Section title="Professionalism Reporting">
          <Card>
            <Row icon={AlertCircle} title="Professionalism report" subtitle="Exemplary professional conduct, or a lapse in it, by faculty, trainees or staff. Submitted through REDCap to the department's professionalism process." href={REDCAP_FORM} />
          </Card>
        </Section>

        <Section title="Official BCM Reporting Channels">
          <Card>
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 bg-white rounded-xl border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img src={`${process.env.PUBLIC_URL}/bcm-logo.png`} alt="Baylor College of Medicine" className="w-9 h-9 object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[15px] text-gray-900 dark:text-white">BCM Integrity Hotline</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">Compliance, ethics, legal and safety matters, or any concern to be raised outside the department. Operated by the BCM Office of Compliance. Confidential; anonymous reporting available; 24 hours a day.</div>
              </div>
            </div>
            <div className="px-4 py-3 flex flex-wrap gap-2">
              <a href={`tel:${HOTLINE}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}><Phone size={15} /> Call {HOTLINE}</a>
              <a href={HOTLINE_PORTAL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border-2 border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100"><ExternalLink size={15} /> Report online</a>
            </div>
          </Card>
        </Section>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 animate-fadeIn" onClick={() => !sending && setOpen(false)}>
          <div role="dialog" aria-label="Quick note or kudos" onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-slide-up max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 pt-4 pb-1">
              <h2 className="font-display text-lg font-semibold text-gray-900 dark:text-white">Quick note or kudos</h2>
              <button onClick={() => setOpen(false)} disabled={sending} aria-label="Close" className="p-2 -m-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"><X size={20} /></button>
            </div>
            {sent ? (
              <div className="px-5 py-8 text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 flex items-center justify-center mx-auto mb-4"><Send size={26} /></div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Sent</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Thank you for your feedback.</p>
              </div>
            ) : (
              <div className="px-5 pt-2 pb-4 space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-snug">Anonymous unless a name is provided.</p>
                <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-900" role="tablist">
                  {[['praise', 'Kudos', ThumbsUp], ['concern', 'Concern', AlertCircle]].map(([id, label, Icon]) => (
                    <button key={id} type="button" role="tab" aria-selected={tone === id} onClick={() => setTone(id)} className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors ${tone === id ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}><Icon size={15} /> {label}</button>
                  ))}
                </div>
                <div>
                  <label className="eyebrow mb-1.5 block">About</label>
                  <select value={about} onChange={(e) => setAbout(e.target.value)} disabled={sending} className={input}>
                    <option value="hospital">A hospital or facility</option>
                    <option value="faculty">A faculty member</option>
                    <option value="trainee">A trainee</option>
                  </select>
                </div>
                <div>
                  <label className="eyebrow mb-1.5 block">Feedback</label>
                  <textarea value={text} onChange={(e) => setText(e.target.value)} disabled={sending} rows={5} placeholder={tone === 'praise' ? 'Describe what went well and who was responsible.' : 'Describe what happened and what should have occurred.'} className={`${input} resize-none`} />
                </div>
                <div>
                  <label className="eyebrow mb-1.5 block">Your name, for follow-up</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} disabled={sending} placeholder="Optional" className={input} />
                </div>
                <button onClick={submit} disabled={sending || !text.trim()} className="w-full text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: ACCENT }}>
                  {sending ? <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /><span>Sending…</span></> : <><Send size={18} /><span>Send</span></>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConcernsScreen;
